import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme, fonts } from "@/theme";
import { Icon } from "@/components/Icon";
import { Screen, TopBar, Loader } from "@/components/ui";
import { useAuth } from "@/context/AuthContext";
import { labApi } from "@/data/api";

export default function LabHome() {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [landing, setLanding] = useState(null);
  const [experiments, setExperiments] = useState([]);
  const [completed, setCompleted] = useState(new Set());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    labApi.home().then(async (r) => {
      setLanding(r.landing);
      setExperiments(r.experiments);
      if (token) {
        const results = await Promise.all(r.experiments.map((e) => labApi.getAnswers(e.slug, token).catch(() => ({ completed: false }))));
        const done = new Set();
        r.experiments.forEach((e, i) => { if (results[i].completed) done.add(e.slug); });
        setCompleted(done);
      }
    }).catch(() => setExperiments([])).finally(() => setLoading(false));
  }, [token]);

  return (
    <Screen>
      <TopBar title={landing?.title ?? "The Weirdo Content Lab 🧪"} subtitle={landing?.subtitle} />
      {loading ? <Loader /> : (
        <div className="ritzy-fade" style={{ padding: "12px 16px 32px" }}>
          {landing?.intro ? (
            <div style={{ background: colors.surfaceSecondary, border: `1px solid ${colors.border}`, borderRadius: 16, padding: 16, display: "flex", flexDirection: "column", gap: 12 }}>
              {landing.intro.split("\n\n").map((p, i) => <div key={i} style={{ color: colors.onSurfaceSecondary, fontSize: 14, lineHeight: "21px", fontFamily: fonts.text }}>{p}</div>)}
            </div>
          ) : null}

          {user?.is_admin ? (
            <button data-testid="lab-manage" className="pressable" onClick={() => navigate("/admin/lab")} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, background: colors.brand, color: colors.onBrand, border: "none", borderRadius: 12, padding: "11px", marginTop: 14, cursor: "pointer", fontSize: 14, fontWeight: 700, fontFamily: fonts.text }}>
              <Icon name="edit-3" size={14} color={colors.onBrand} />Manage the Lab
            </button>
          ) : null}

          <button data-testid="lab-quick-help" className="pressable" onClick={() => navigate("/content")} style={{ width: "100%", textAlign: "left", background: colors.brand, border: "none", borderRadius: 16, padding: 18, marginTop: 14, cursor: "pointer" }}>
            <div style={{ color: colors.onBrand, fontSize: 17, fontFamily: fonts.display, fontWeight: 700 }}>{landing?.quick_title ?? "🆘 My brain has no content today"}</div>
            <div style={{ color: colors.onBrand, fontSize: 13, marginTop: 4, opacity: 0.9, fontFamily: fonts.text }}>{landing?.quick_subtitle ?? "Give me something to post."}</div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 12 }}><Icon name="arrow-right" size={16} color={colors.onBrand} /><span style={{ color: colors.onBrand, fontSize: 14, fontWeight: 700, fontFamily: fonts.text }}>Open the Content Vault</span></div>
          </button>

          <div style={{ color: colors.onSurface, fontSize: 18, fontFamily: fonts.display, fontWeight: 700, margin: "22px 0 12px" }}>Experiments</div>
          {experiments.map((e) => {
            const done = completed.has(e.slug);
            return (
              <button key={e.id} data-testid={`lab-exp-${e.slug}`} className="pressable" onClick={() => navigate(`/lab/${e.slug}`)} style={{ width: "100%", display: "flex", alignItems: "center", gap: 12, background: colors.surfaceSecondary, border: `1px solid ${colors.border}`, borderRadius: 16, padding: 14, marginBottom: 10, cursor: "pointer", textAlign: "left" }}>
                <div style={{ width: 40, height: 40, borderRadius: 12, background: colors.brandTertiary, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, color: colors.onBrandTertiary, fontSize: 15, fontFamily: fonts.display, fontWeight: 800 }}>{e.number}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ color: colors.onSurface, fontSize: 16, fontWeight: 700, fontFamily: fonts.text }}>{e.title}</div>
                  <div style={{ color: colors.muted, fontSize: 12, marginTop: 2, lineHeight: "17px", fontFamily: fonts.text }}>{e.subtitle}</div>
                </div>
                <Icon name={done ? "check-circle" : "chevron-right"} size={20} color={done ? colors.brand : colors.muted} />
              </button>
            );
          })}
        </div>
      )}
    </Screen>
  );
}
